import { Box, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { CloudUpload, Description, Close } from '@material-ui/icons';
import { Layout } from '@roc/client-portal-shared/components';
import { LoginLogoImg } from '@roc/feature-app-core';
import { Button, Paper, VerticalBarLoader } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router';
import { FundingDocsService } from '../../services';
import { DEFAULT_LINK_ERROR, DEFAULT_UPLOAD_ERROR, getApiErrorMessage } from './fundingDocsUtils';

const fundingDocsService = new FundingDocsService();

const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    titleBox: {
      textAlign: 'center',
    },
    paper: {
      padding: theme.spacing(6, 4),
      borderRadius: theme.spacing(2),
      boxShadow:
        'rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px',
    },
    uploadSection: {
      textAlign: 'left',
      marginTop: theme.spacing(3),
    },
    instructionsText: {
      marginBottom: theme.spacing(2),
    },
    actions: {
      marginTop: theme.spacing(2),
    },
    dragArea: {
      border: '2px dotted #427ED1',
      width: '100%',
      minHeight: 120,
      backgroundColor: '#EAF4FA',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1),
      cursor: 'pointer',
    },
    dragAreaIcon: {
      width: '3vw',
      height: '3vw',
      minWidth: 20,
      minHeight: 20,
      color: '#4B89DA',
    },
    fileRow: {
      backgroundColor: '#EAF4FA',
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1, 2),
      margin: theme.spacing(1, 0),
      width: '100%',
    },
    fileName: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

export type FundingDocsParams = {
  token: string;
};

export const FundingDocs = observer(() => {
  const classes = useStyles();
  const { token } = useParams<FundingDocsParams>();

  const [loading, setLoading] = useState(true);
  const [loanNotFound, setLoanNotFound] = useState<string | null>(null);
  const [loanId, setLoanId] = useState<string | null>(null);
  const [uploads, setUploads] = useState<File[]>([]);
  const [uploaded, setUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileValidationError, setFileValidationError] = useState(false);

  useEffect(() => {
    validateLink();
  }, [token]);

  const validateLink = async () => {
    setLoading(true);
    setLoanNotFound(null);
    setLoanId(null);
    if (!token) {
      setLoanNotFound(DEFAULT_LINK_ERROR);
      setLoading(false);
      return;
    }
    try {
      const response = await fundingDocsService.validateFundingDocsLink(token);
      const validatedLoanId = response?.data?.data;
      validatedLoanId ? setLoanId(String(validatedLoanId)) : setLoanNotFound(DEFAULT_LINK_ERROR);
    } catch (error) {
      setLoanNotFound(getApiErrorMessage(error, DEFAULT_LINK_ERROR));
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    setFileValidationError(false);
    setUploads(prevUploads => [...prevUploads, ...acceptedFiles]);
  };

  const onDropRejected = () => {
    setFileValidationError(true);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: true,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE_BYTES,
  });

  const handleRemoveUpload = (index: number) => {
    setUploads(prevUploads => prevUploads.filter((_, i) => i !== index));
  };

  const handleCancel = () => {
    setUploads([]);
  };

  const handleUpload = async () => {
    setUploading(true);
    setUploadError(null);
    try {
      const response = await fundingDocsService.uploadFundingDocs(token, uploads);
      response?.data?.data ? setUploaded(true) : setUploadError(DEFAULT_UPLOAD_ERROR);
    } catch (error) {
      setUploadError(getApiErrorMessage(error, DEFAULT_UPLOAD_ERROR));
    } finally {
      setUploading(false);
    }
  };

  const canUpload = uploads.length > 0;

  return (
    <Layout maxWidth="md">
      <Paper className={classes.paper}>
        <LoginLogoImg imgStyle={{ maxWidth: 300 }} />
        <Box mb={4} className={classes.titleBox}>
          {loanNotFound ?
            <Alert severity="warning">{loanNotFound}</Alert> :
            <Typography variant="h3">
              {`Upload Funding Docs: Loan #${loanId ?? ''}`}
            </Typography>
          }
        </Box>
        {loading && <VerticalBarLoader />}
        {!loading && !loanNotFound && uploaded && (
          <Box className={classes.uploadSection}>
            <Alert severity="success">File(s) uploaded successfully. You can now close this page.</Alert>
          </Box>
        )}
        {!loading && !loanNotFound && !uploaded && (
          <Box className={classes.uploadSection}>
            <Typography variant="subtitle1" className={classes.instructionsText}>
              Please upload the funding document(s) for this loan below.
            </Typography>
            <Typography variant="caption" component="p" color="textSecondary" className={classes.instructionsText}>
              Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG. Maximum size: 10MB per file.
            </Typography>
            {fileValidationError && (
              <Alert severity="error" className={classes.instructionsText}>
                One or more files were rejected. Accepted formats are PDF, DOC, DOCX, JPG, JPEG and PNG, with a maximum size of 10MB each.
              </Alert>
            )}
            {uploadError && (
              <Alert severity="error" className={classes.instructionsText}>
                {uploadError}
              </Alert>
            )}
            <Box {...getRootProps()} className={classes.dragArea}>
              <input {...getInputProps()} />
              <CloudUpload className={classes.dragAreaIcon} />
              {isDragActive ? (
                <p>Drop the file(s) here...</p>
              ) : (
                <>
                  <p>Drop files here to upload</p>
                  <p>or click here</p>
                </>
              )}
            </Box>
            {uploads.map((file, index) => (
              <Grid
                container
                key={`${file.name}-${index}`}
                spacing={2}
                alignItems="center"
                className={classes.fileRow}
              >
                <Grid item xs={12} sm={1}>
                  <Description />
                </Grid>
                <Grid item xs={9} sm={10}>
                  <Typography className={classes.fileName}>{file.name}</Typography>
                </Grid>
                <Grid item xs={3} sm={1}>
                  <Close style={{ cursor: 'pointer' }} onClick={() => handleRemoveUpload(index)} />
                </Grid>
              </Grid>
            ))}
            <Grid container justify="flex-end" spacing={2} className={classes.actions}>
              <Grid item>
                <Button
                  testId="cancelFundingDocs"
                  variant="outlined"
                  color="primary"
                  disabled={uploads.length === 0 || uploading}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  testId="uploadFundingDocs"
                  variant="contained"
                  color="primary"
                  disabled={!canUpload || uploading}
                  onClick={handleUpload}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Layout>
  );
});

export default FundingDocs;
